/**
 * Код с QR-наклейки оборудования: формат, нормализация, разбор ссылки.
 *
 * Вынесено из EquipmentScanner отдельным модулем, потому что этим кодом
 * пользуются четыре разных экрана, включая ПУБЛИЧНЫЙ мини-лендинг, у которого
 * нет ни сессии, ни доступа к api-клиенту. Держать нормализацию в компоненте
 * сканера значило бы либо тащить за собой его зависимости, либо завести вторую
 * копию правил — а правила здесь такие, что вторая копия обязательно разъедется
 * с первой на первой же правке.
 *
 * Серверная сторона нормализует ввод сама (services/equipmentTags.js
 * normalizeToken), здесь то же самое делается заранее — чтобы не гонять
 * заведомо мусорный запрос и чтобы человек видел код в привычном виде.
 */

/** Человекочитаемая форма: три группы по четыре, как напечатано под QR. */
export function formatCode(token) {
  return String(token || '').replace(/(.{4})(.{4})(.{4})/, '$1-$2-$3');
}

/**
 * Прогрессивная группировка для ПОЛЯ ВВОДА: дефисы появляются по мере набора,
 * а не только на полном коде.
 *
 * ⚠️ Отличается от `formatCode` осознанно. Там регулярка требует ровно 12
 * символов и на «HGJF» не срабатывает вовсе — для готового токена это верно,
 * для живого ввода означало бы, что дефисы выскакивают все разом на последнем
 * символе. Человек в этот момент сверяет набранное с наклейкой, где код
 * напечатан группами.
 */
export function groupCode(raw) {
  const s = String(raw || '').slice(0, 12);
  return s.replace(/(.{4})(?=.)/g, '$1-');
}

/**
 * Привести то, что человек вбил руками, к алфавиту кода.
 *
 * ⚠️ Похожие символы не отвергаем, а ЧИНИМ. Алфавит кода (Crockford Base32)
 * не содержит I, L, O и U именно затем, чтобы их нельзя было спутать, — но
 * человек стоит перед печью и переписывает затёртую наклейку, и он напишет
 * «O» вместо нуля. Отказ «неверный код» в этой точке — это несостоявшийся
 * выезд, а не забота о чистоте ввода.
 */
export function normalizeCode(input) {
  return String(input || '').toUpperCase().replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0').replace(/I/g, '1').replace(/L/g, '1').replace(/U/g, 'V');
}

/**
 * Токен из нашей ссылки или из введённого руками кода. null — не наше.
 *
 * Чужие QR отсекаем здесь, а не запросом к серверу: сканер ловит в кадр всё
 * подряд, включая наклейку курьера на соседней коробке.
 */
export function extractToken(text) {
  const s = String(text || '').trim();
  // Формат: https://<sub>.fixty.io/e/<TOKEN> — принимаем и без схемы, и с
  // произвольным поддоменом, включая кастомные домены на будущее.
  const m = s.match(/\/e\/([0-9A-Za-z-]{8,20})\/?$/);
  if (m) return normalizeCode(m[1]);
  // Человек мог ввести один код руками, без ссылки.
  if (/^[0-9A-Za-z-]{12,15}$/.test(s)) return normalizeCode(s);
  return null;
}
